import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  ChevronLeft,
  Database,
  ShieldCheck,
  Users,
  Workflow,
  KeyRound,
  Layers,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react'

// Headline A/B candidates — "primary" is live. Swap the `active` flag to test
// an alternate; keep the losers here for the next round rather than deleting.
const headlines = [
  { id: 'primary', active: true, text: 'Build RAG on your own data — without shipping your own data.' },
  { id: 'alt-enclave', active: false, text: 'Retrieval-augmented generation that never leaves your enclave.' },
  { id: 'alt-cloud', active: false, text: "The RAG pipeline for people who can't just upload everything to a vendor's cloud." },
]
const headline = headlines.find((h) => h.active) ?? headlines[0]

const readiness = {
  available: { label: 'Available', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  pilot: { label: 'Pilot', className: 'bg-ember-500/15 text-ember-300 border-ember-500/30' },
}

const capabilities = [
  {
    icon: Database,
    status: 'available',
    title: 'Governed ingest, embedding, and local vector search',
    description:
      'Multi-format document ingest → normalize → embed → local vector store — the actual RAG backbone, running where your data already is. The highest-confidence subsystem in our own architecture record.',
  },
  {
    icon: ShieldCheck,
    status: 'available',
    title: 'A compliance rulebook that runs at ingest time',
    description:
      '13 live rules spanning PCI-DSS, HIPAA, PII (NIST SP 800-122), and CCPA/CPRA, writing to one canonical audit log with six-year retention on PHI-relevant records — not a policy document nobody checks against.',
  },
  {
    icon: Users,
    status: 'pilot',
    title: 'Entity resolution with a human in the loop',
    description:
      'Fuzzy and exact matching feeds a review queue for anything ambiguous. Low-confidence matches route to a person instead of being silently auto-resolved.',
  },
  {
    icon: Workflow,
    status: 'pilot',
    title: 'Multi-step workflow orchestration',
    description:
      'Airflow-based workflows (Conductor) chain ingest → enrichment → review → delivery, so a RAG pipeline becomes a governed, repeatable process instead of a notebook.',
  },
  {
    icon: Layers,
    status: 'pilot',
    title: 'Agent governance',
    description:
      'A single versioned policy source bounds what an AI agent may say or do, with a decision gate that escalates uncertain or risky actions to a person instead of letting the agent act alone.',
  },
  {
    icon: KeyRound,
    status: 'pilot',
    title: 'Identity Anchor',
    description:
      "Every unit proves who it is with a key only it holds, and answers to one accountable, verified operator — no anchor, no machine. It's our own approach to binding accountability to hardware; we're not publishing the internals yet.",
  },
]

const comparisonRows = [
  { label: 'Where readable data lives', cloud: 'Vendor cloud', diy: 'Your infrastructure', graffiti: 'Your enclave or edge unit' },
  { label: 'Governance', cloud: 'Vendor-defined', diy: 'Built in-house', graffiti: 'Pre-seeded rules, audit, human review' },
  { label: 'Time to first use', cloud: 'Fast', diy: 'Slow', graffiti: 'Fast, via packaged Tribes' },
  { label: 'Cost shape', cloud: 'Usage-variable', diy: 'Capital + staff', graffiti: 'Low fixed license + optional usage' },
]

export default function RAGWorkflowsPage() {
  return (
    <div className="pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/products/enterprise"
          className="inline-flex items-center gap-2 text-void-400 hover:text-void-200 transition-colors mb-8"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Enterprise Solutions
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-void-50 mb-6 max-w-3xl">
            {headline.text}
          </h1>
          <p className="text-xl text-void-400 max-w-2xl mb-12 leading-relaxed">
            Most RAG stacks start the same way: documents go up to a cloud vector database, embeddings
            get generated by someone else's API, and your source material — contracts, patient
            records, financials, case files — now lives somewhere you don't control. Graffiti™ OS runs
            that same pipeline where your data already is.
          </p>

          <div className="mb-10 flex flex-wrap gap-4 text-sm">
            {Object.entries(readiness).map(([key, { label, className }]) => (
              <span key={key} className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${className}`}>
                {label}
              </span>
            ))}
            <span className="text-void-500">— founder-assessed per component, not an independent attestation.</span>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {capabilities.map((cap, i) => (
              <motion.div
                key={cap.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="p-6 rounded-2xl bg-void-900/30 border border-void-800/50"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="inline-flex p-2.5 rounded-xl bg-synth-500/10">
                    <cap.icon className="w-5 h-5 text-synth-400" />
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium shrink-0 ${readiness[cap.status].className}`}>
                    {readiness[cap.status].label}
                  </span>
                </div>
                <h3 className="text-lg font-display font-semibold text-void-100 mb-2">{cap.title}</h3>
                <p className="text-void-400 text-sm leading-relaxed">{cap.description}</p>
              </motion.div>
            ))}
          </div>

          <h2 className="text-2xl font-display font-bold text-void-50 mb-4">
            Why this matters for running several AI systems
          </h2>
          <p className="text-void-300 leading-relaxed mb-16 max-w-3xl">
            You're not locked into one model vendor. What leaves the enclave is metered and
            license-gated — abstracted, de-identified context, not raw source data — and it's
            swappable across model providers by design. Point the same governed pipeline at
            different inference backends for different tasks instead of re-architecting your data
            pipeline every time you change providers.
          </p>

          <h2 className="text-2xl font-display font-bold text-void-50 mb-6">The honest comparison</h2>
          <div className="overflow-x-auto mb-16 rounded-2xl border border-void-800/50">
            <table className="w-full text-sm text-left">
              <thead className="bg-void-900/60">
                <tr>
                  <th className="px-4 py-3 text-void-200 font-semibold"></th>
                  <th className="px-4 py-3 text-void-200 font-semibold">Cloud AI service</th>
                  <th className="px-4 py-3 text-void-200 font-semibold">DIY on-premises</th>
                  <th className="px-4 py-3 text-synth-300 font-semibold">Graffiti™ OS</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.label} className="border-t border-void-800/40">
                    <td className="px-4 py-3 text-void-200 font-medium">{row.label}</td>
                    <td className="px-4 py-3 text-void-400">{row.cloud}</td>
                    <td className="px-4 py-3 text-void-400">{row.diy}</td>
                    <td className="px-4 py-3 text-synth-300">{row.graffiti}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-2xl bg-void-900/30 border border-void-800/50 p-8 mb-10">
            <h2 className="text-2xl font-display font-bold text-void-50 mb-4">Entry point</h2>
            <ul className="space-y-2 mb-6">
              {[
                'Trial — $0 for 14 days',
                'Starter — $19/mo',
                'Professional — $49/mo',
                'Scrambler hardware (optional) — Base $799',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-void-300">
                  <CheckCircle2 className="w-4 h-4 text-synth-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <a
              href="https://23controls.com/signup"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-synth-600 hover:bg-synth-500 text-white font-medium transition-colors"
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <p className="text-void-500 text-sm max-w-2xl">
            Readiness levels above are founder-assessed as of our most recent executive briefing, not
            independent attestations. We don't claim SOC 2, HITRUST, or similar certifications we
            haven't obtained, and nothing on this page represents a capability still in design or on
            the roadmap as available today.
          </p>
        </motion.div>
      </div>
    </div>
  )
}
